import { ref } from 'vue'

const SCRIPT_SRC = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js'
const SCRIPT_ID = 'daum-postcode-sdk'
const LAYER_ID = 'daum-postcode-layer'

export interface PostcodeResult {
  /** 5자리 우편번호 */
  zipcode: string
  /** 도로명 주소 (없으면 지번 주소) */
  address: string
  /** 건물명 등 참고 항목. 상세주소 입력을 도우려고 함께 넘긴다. */
  extra: string
}

/** 다음 우편번호 서비스가 oncomplete 로 넘겨주는 값 중 쓰는 것만 추린 타입 */
interface DaumPostcodeData {
  zonecode: string
  roadAddress: string
  jibunAddress: string
  userSelectedType: 'R' | 'J'
  bname: string
  buildingName: string
  apartment: 'Y' | 'N'
}

interface DaumPostcodeInstance {
  open: (options?: { popupTitle?: string }) => void
  embed: (element: HTMLElement) => void
}

declare global {
  interface Window {
    daum?: {
      Postcode: new (options: {
        oncomplete: (data: DaumPostcodeData) => void
        onclose?: (state: string) => void
        width?: string | number
        height?: string | number
        animation?: boolean
      }) => DaumPostcodeInstance
    }
  }
}

let loading: Promise<void> | null = null

/** 스크립트는 처음 검색을 누를 때 한 번만 받는다. */
function loadScript(): Promise<void> {
  if (window.daum?.Postcode) {
    return Promise.resolve()
  }

  if (loading) {
    return loading
  }

  loading = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    const script = existing ?? document.createElement('script')

    script.addEventListener('load', () => resolve(), { once: true })
    script.addEventListener(
      'error',
      () => {
        loading = null
        script.remove()
        reject(new Error('우편번호 서비스를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'))
      },
      { once: true },
    )

    if (!existing) {
      script.id = SCRIPT_ID
      script.src = SCRIPT_SRC
      script.async = true
      document.head.appendChild(script)
    }
  })

  return loading
}

/** 도로명 주소 뒤에 붙일 참고항목(법정동·건물명)을 다음 권장 방식대로 조립한다. */
function buildExtra(data: DaumPostcodeData): string {
  if (data.userSelectedType !== 'R') {
    return ''
  }

  const parts: string[] = []

  if (data.bname && /[동로가]$/g.test(data.bname)) {
    parts.push(data.bname)
  }

  if (data.buildingName && data.apartment === 'Y') {
    parts.push(data.buildingName)
  }

  return parts.length > 0 ? `(${parts.join(', ')})` : ''
}

function removeLayer() {
  document.getElementById(LAYER_ID)?.remove()
}

/**
 * 화면 너비에 맞춰 레이어 크기를 잡는다.
 * - 좁은 화면: 거의 전체 화면
 * - 넓은 화면: 가운데 카드형
 */
function createResponsiveLayer(): { root: HTMLElement; box: HTMLElement } {
  removeLayer()

  const narrow = window.innerWidth < 720
  const root = document.createElement('div')
  root.id = LAYER_ID
  root.setAttribute('role', 'dialog')
  root.setAttribute('aria-label', '주소 검색')
  root.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:10000',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'padding:env(safe-area-inset-top,0) env(safe-area-inset-right,0) env(safe-area-inset-bottom,0) env(safe-area-inset-left,0)',
    'background:rgba(14,34,52,0.5)',
    'box-sizing:border-box',
  ].join(';')

  const panel = document.createElement('div')
  panel.style.cssText = narrow
    ? [
        'position:relative',
        'width:100%',
        'height:100%',
        'max-width:100%',
        'max-height:100%',
        'background:#fff',
        'display:flex',
        'flex-direction:column',
        'overflow:hidden',
      ].join(';')
    : [
        'position:relative',
        'width:min(520px,94vw)',
        'height:min(640px,88vh)',
        'background:#fff',
        'border-radius:16px',
        'box-shadow:0 16px 48px rgba(14,34,52,0.28)',
        'display:flex',
        'flex-direction:column',
        'overflow:hidden',
      ].join(';')

  const head = document.createElement('div')
  head.style.cssText = [
    'flex:0 0 auto',
    'display:flex',
    'align-items:center',
    'justify-content:space-between',
    'gap:12px',
    'padding:14px 16px',
    'border-bottom:1px solid #e4ecef',
    'font-family:Pretendard,-apple-system,BlinkMacSystemFont,sans-serif',
  ].join(';')

  const title = document.createElement('strong')
  title.textContent = '주소 검색'
  title.style.cssText = 'font-size:16px;color:#142c47;font-weight:600'

  const closeBtn = document.createElement('button')
  closeBtn.type = 'button'
  closeBtn.textContent = '닫기'
  closeBtn.style.cssText = [
    'border:0',
    'background:#eef3f5',
    'color:#3f5966',
    'border-radius:999px',
    'padding:8px 14px',
    'font:600 13px Pretendard,-apple-system,sans-serif',
    'cursor:pointer',
    'min-height:40px',
  ].join(';')

  const box = document.createElement('div')
  box.style.cssText = 'flex:1 1 auto;min-height:0;width:100%'

  head.appendChild(title)
  head.appendChild(closeBtn)
  panel.appendChild(head)
  panel.appendChild(box)
  root.appendChild(panel)
  document.body.appendChild(root)

  return { root, box }
}

export function useDaumPostcode() {
  const opening = ref(false)

  /** 검색창을 띄우고, 사용자가 주소를 고르면 그 값을 돌려준다. 그냥 닫으면 null. */
  const searchPostcode = async (): Promise<PostcodeResult | null> => {
    if (opening.value) {
      return null
    }

    opening.value = true

    try {
      await loadScript()
    } catch (error) {
      opening.value = false
      throw error
    }

    return new Promise<PostcodeResult | null>((resolve) => {
      let settled = false
      let picked: PostcodeResult | null = null
      const { root, box } = createResponsiveLayer()

      const finish = () => {
        if (settled) return
        settled = true
        removeLayer()
        opening.value = false
        resolve(picked)
      }

      root.addEventListener('click', (e) => {
        if (e.target === root) finish()
      })
      root.querySelector('button')?.addEventListener('click', finish)

      new window.daum!.Postcode({
        width: '100%',
        height: '100%',
        animation: false,
        oncomplete(data) {
          picked = {
            zipcode: data.zonecode,
            address: data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress,
            extra: buildExtra(data),
          }
          finish()
        },
        onclose() {
          finish()
        },
      }).embed(box)
    })
  }

  return { opening, searchPostcode }
}
